import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import type { Moderator } from "@/services/moderators/generated/model";
import { RoleEnum } from "@/services/moderators/generated/model";
import {
  getModeratorsListQueryKey,
  useModeratorsActivateCreate,
  useModeratorsCreate,
  useModeratorsDestroy,
  useModeratorsList,
} from "@/services/moderators/generated/moderators-moderators/moderators-moderators";
import "./ModeratorPage.css";
import "./ModeratorsPage.css";

const ModeratorsPage = () => {
  const { t } = useTranslation("moderator");
  return (
    <ModeratorLayout>
      {(me) => {
        if (me.role !== "SUPERMODERATOR") {
          return (
            <>
              <header className="mod-page__header">
                <div>
                  <h1 className="mod-page__title">{t("moderators.title")}</h1>
                  <p className="mod-page__subtitle">{t("shared.superOnly")}</p>
                </div>
              </header>
              <div className="mod-page__empty">{t("shared.restricted")}</div>
            </>
          );
        }
        return <ModeratorsContent currentModeratorId={me.id} />;
      }}
    </ModeratorLayout>
  );
};

const ModeratorsContent = ({ currentModeratorId }: { currentModeratorId: number }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const queryParams = search.trim() ? { search: search.trim() } : undefined;
  const { data, isLoading, error } = useModeratorsList(queryParams);
  const moderators = unwrapPaginated<Moderator>(data);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: getModeratorsListQueryKey(queryParams) });

  const createMutation = useModeratorsCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        setShowCreate(false);
      },
    },
  });
  const destroyMutation = useModeratorsDestroy({ mutation: { onSuccess: invalidate } });
  const activateMutation = useModeratorsActivateCreate({ mutation: { onSuccess: invalidate } });

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("moderators.title")}</h1>
          <p className="mod-page__subtitle">{t("moderators.subtitle")}</p>
        </div>
        <button type="button" className="mod-page__btn mod-page__btn--primary" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? t("common:actions.cancel") : t("moderators.add")}
        </button>
      </header>

      {showCreate ? (
        <CreateForm
          onSubmit={(payload) => createMutation.mutate({ data: payload })}
          isSubmitting={createMutation.isPending}
          error={createMutation.isError ? extractError(createMutation.error, t("moderators.createError")) : null}
        />
      ) : null}

      <form
        className="mod-page__toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchInput);
        }}
      >
        <input
          type="search"
          className="mod-page__input"
          placeholder={t("moderators.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="mod-page__btn mod-page__btn--primary">
          {t("common:actions.apply")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          onClick={() => {
            setSearchInput("");
            setSearch("");
          }}
        >
          {t("companies.reset")}
        </button>
      </form>

      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("moderators.loadError")}</div> : null}
      {!isLoading && moderators.length === 0 ? <div className="mod-page__empty">{t("moderators.empty")}</div> : null}

      {moderators.map((m) => {
        const isSelf = m.id === currentModeratorId;
        const isSuper = m.role === RoleEnum.SUPERMODERATOR;
        return (
          <article key={m.id} className="mod-page__card mod-moderators__card">
            <div className="mod-page__card-body">
              <h2 className="mod-page__card-title">{m.fullName}</h2>
              <p className="mod-page__card-meta">
                {m.email} {m.phoneNumber ? `· ${m.phoneNumber}` : ""}
              </p>
              <p className="mod-page__card-meta">
                <span className={`mod-page__chip ${isSuper ? "mod-page__chip--success" : ""}`}>
                  {isSuper ? t("shared.roleSuperModerator") : t("shared.roleModerator")}
                </span>
                <span className={`mod-page__chip ${m.isActive ? "mod-page__chip--success" : "mod-page__chip--danger"}`}>
                  {m.isActive ? t("shared.active") : t("shared.inactive")}
                </span>
              </p>
            </div>
            <div className="mod-moderators__actions">
              <Link to={`/moderator/moderators/${m.id}`} className="mod-page__btn mod-page__btn--primary">
                {t("companies.open")}
              </Link>
              <button
                type="button"
                className="mod-page__btn mod-page__btn--ghost"
                onClick={() => activateMutation.mutate({ id: m.id, data: { isActive: !m.isActive } })}
                disabled={isSelf || (activateMutation.isPending && activateMutation.variables?.id === m.id)}
              >
                {m.isActive ? t("shared.deactivate") : t("shared.activate")}
              </button>
              <button
                type="button"
                className="mod-page__btn mod-page__btn--danger"
                onClick={() => {
                  if (window.confirm(t("shared.deleteModeratorConfirm", { name: m.fullName }))) {
                    destroyMutation.mutate({ id: m.id });
                  }
                }}
                disabled={isSelf || (destroyMutation.isPending && destroyMutation.variables?.id === m.id)}
              >
                {t("common:actions.delete")}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
};

interface CreateFormProps {
  onSubmit: (payload: {
    fullName: string;
    email: string;
    password: string;
    phoneNumber?: string;
    role?: typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR;
  }) => void;
  isSubmitting: boolean;
  error: string | null;
}

const CreateForm = ({ onSubmit, isSubmitting, error }: CreateFormProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR>(RoleEnum.MODERATOR);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !password.trim()) return;
    onSubmit({
      fullName: fullName.trim(),
      email: email.trim(),
      password,
      phoneNumber: phoneNumber.trim() || undefined,
      role,
    });
  };

  return (
    <form className="mod-page__card mod-moderators__create" onSubmit={submit}>
      <div className="mod-page__form-row">
        <input
          className="mod-page__input"
          placeholder={t("shared.fullName")}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
        <input
          className="mod-page__input"
          type="email"
          placeholder={t("common:labels.email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="mod-page__form-row">
        <input
          className="mod-page__input"
          type="password"
          placeholder={t("moderators.initialPassword")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
        <input
          className="mod-page__input"
          placeholder={t("moderators.phoneOptional")}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
        <select
          className="mod-page__select"
          value={role}
          onChange={(e) => setRole(e.target.value as typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR)}
        >
          <option value={RoleEnum.MODERATOR}>{t("shared.roleModerator")}</option>
          <option value={RoleEnum.SUPERMODERATOR}>{t("shared.roleSuperModerator")}</option>
        </select>
      </div>
      {error ? <p className="mod-page__inline-error">{error}</p> : null}
      <div className="mod-page__form-row">
        <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={isSubmitting}>
          {isSubmitting ? t("moderators.creating") : t("common:actions.create")}
        </button>
      </div>
    </form>
  );
};

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: Record<string, unknown> }; data?: Record<string, unknown> } | null | undefined;
  const body = e?.response?.data ?? e?.data;
  if (body) {
    if (typeof body.error === "string") return body.error;
    for (const v of Object.values(body)) {
      if (typeof v === "string") return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

export default ModeratorsPage;
